<template>
  <div class="account-subscription-balance">
    <BasePreloader v-if="loading.tariffData || loading.history" class="full-width" />

    <template v-else>
      <div class="account-subscription-balance__description-block">
        <div class="account-subscription-balance__description-header">
          <div class="account-subscription-balance__description-tariff">
            <div class="account-subscription-balance__description-tariff-title-wrap">
              <div>{{ $tAccountSubscription('current_tariff_plan') }}</div>
              <div class="account-subscription-balance__description-tariff-title">{{ tariff.name }}</div>
            </div>
            <div class="account-subscription-balance__description-tariff-price-wrap">
              <div>{{ $tAccountSubscription('tariff_cost') }}</div>
              <div class="account-subscription-balance__description-tariff-price">{{ tariff.price }}</div>
            </div>
          </div>
          <div class="account-subscription-balance__description-toolbar">
            <div class="account-subscription-balance__description-toolbar-item">
              <div>{{ $tAccountSubscription('tariff_valid_until') }}</div>
              <div>{{ tariff.period_to }}</div>
            </div>
            <div class="account-subscription-balance__description-toolbar-item">
              <div>{{ $tAccountSubscription('days_left') }}</div>
              <div>{{ tariff.days_to }}</div>
            </div>
            <div class="account-subscription-balance__description-toolbar-item">
              <div>{{ $tAccountSubscription('renew_automatically') }}</div>
              <div>
                <SwitchCheckbox
                  :value="renewAutomatically"
                  class="_blue"
                />
              </div>
            </div>
            <button
              class="base-btn base-btn--blue"
              disabled
            >
              {{ $tAccountSubscription('extend_tariff') }}
            </button>
          </div>
        </div>

        <div
          class="account-subscription-balance__description-list"
          :class="{ _expanded: expandedList }"
        >
          <CollapseTransition>
            <div class="account-subscription-balance__description-list-items-wrap">
              <div
                v-for="(item, index) in descriptionList"
                :key="index"
                class="account-subscription-balance__description-list-item"
              >
                <div class="account-subscription-balance__description-item-title">
                  <div class="account-subscription-balance__description-item-title-icon">
                    <BaseSvg :name="item.icon.name" :width="item.icon.width" :height="item.icon.height" />
                  </div>
                  <div class="account-subscription__state-title">{{ item.title }}</div>
                </div>

                <div class="account-subscription-balance__description-item-stats">
                  <div class="account-subscription-balance__description-item-stat _activity-stat">
                    <div>{{ item.activity.title }}</div>
                    <div>{{ item.activity.value }}</div>
                  </div>
                  <div class="account-subscription-balance__description-item-stat _available-stat">
                    <div>{{ $tAccountSubscription('available_more') }}</div>
                    <div>{{ item.availableCount }}</div>
                  </div>
                </div>
              </div>
            </div>
          </CollapseTransition>
          <div class="account-subscription-balance__description-list-expand">
            <button
              class="base-btn"
              @click="expandedList = !expandedList"
            >
              <span>{{ $tAccountSubscription(expandedList ? 'collapse_details' : 'expand_details') }}</span>
              <BaseSvg name="ico-arrow" width="0.9rem" height="0.6rem" class="ml-2" />
            </button>
          </div>
        </div>
      </div>

      <div class="account-subscription-balance__history-block">
        <div class="account-subscription-balance__history-header">
          <BaseSvg name="ico-calendar-time-outline" width="2.7rem" height="2.8rem" />
          <div class="account-subscription__state-title">{{ $tAccountSubscription('operations_history') }}</div>
        </div>

        <div class="account-subscription-balance__history-list-wrap">
          <div class="account-subscription-balance__history-list">
            <div
              v-for="(item, index) in historyList"
              :key="index"
              class="account-subscription-balance__history-list-item"
            >
              <div class="account-subscription-balance__history-item-time">
                <time>{{ item.date }}</time>
                <time>{{ item.time }}</time>
              </div>
              <div class="account-subscription-balance__history-item-title">
                {{ item.title }}
              </div>
              <div
                class="account-subscription-balance__history-item-price"
                :style="{ color: item.valueColor }"
              >
                {{ item.value }}
              </div>
              <component
                :is="item.link ? 'router-link-modified' : 'div'"
                class="account-subscription-balance__history-item-link"
                :class="{ _link: item.link }"
                :to="item.link"
              >
                <span>{{ item.linkTitle }}</span>
              </component>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { merge } from 'lodash';
import { CollapseTransition } from 'vue2-transitions';
import SwitchCheckbox from '../../../components/SwitchCheckbox';


const PriceColor = {
  Gray: '#67717f',
  Green: '#10ba54',
  Red: '#fc423b',
};

export default {
  name: 'AccountSubscriptionBalance',
  components: {
    SwitchCheckbox,
    CollapseTransition,
  },
  data() {
    return {
      renewAutomatically: false,
      expandedList: false,
    };
  },
  computed: {
    ...mapGetters('accountSubscription', [
      'loading',
      'tariff',
      'actionsStats',
      'history'
    ]),

    descriptionList() {
      return [
        {
          icon: { name: 'ico-file-outline', width: '1.2rem', height: '1.6rem' },
          title: this.$tAccountSubscription('publications'),
          activity: { title: this.$tAccountSubscription('actively'), value: 0 },
          availableCount: 0,
        },
        {
          icon: { name: 'ico-eye-eyelashes', width: '1.8rem', height: '1.5rem' },
          title: this.$tAccountSubscription('action_open_phones'),
          activity: { title: this.$tAccountSubscription('opened'), value: 0 },
          availableCount: 0,
        },
        {
          icon: { name: 'ico-arrow-up-underlines', width: '0.9rem', height: '2rem' },
          title: this.$tAccountSubscription('action_bumps'),
          activity: { title: this.$tAccountSubscription('used'), value: 0 },
          availableCount: 0,
        },
        {
          icon: { name: 'ico-fire', width: '1.3rem', height: '1.7rem' },
          title: this.$tAccountSubscription('action_hots'),
          activity: { title: this.$tAccountSubscription('currently_used'), value: 0 },
          availableCount: 0,
        },
      ].map(this.bindActionStatsData);
    },
    historyList() {
      const getRelationTitle = (relation) => {
        if (relation.hasOwnProperty('fullname') && !relation.fullname) return '-';
        return relation.fullname || relation.vacancy_title || this.$tAccountSubscription('my_company');
      };

      const getRelationLink = (relation) => {
        if (relation.vacancy_slug) return { name: 'vacancy', params: { vacancyId: relation.vacancy_slug } };
        else if (relation.fullname) return { name: 'received-resume', params: { resumeId: relation.sent_resume_id } };
        return false;
      };

      return this.history.map(item => {
        const [date, time] = item.date.split(' ');

        return {
          date,
          time,
          title: this.$tAccountSubscription(item.type),
          value: item.price,
          valueColor: PriceColor.Gray,
          linkTitle: getRelationTitle(item.relation),
          link: getRelationLink(item.relation),
        };
      });
    },
  },
  created() {
    this.load();
  },
  methods: {
    ...mapActions('accountSubscription', [
      'loadTariffData',
      'loadHistory',
    ]),

    load() {
      this.loadTariffData();
      this.loadHistory();
    },
    bindActionStatsData(descriptionObj, index) {
      if (!this.actionsStats) return descriptionObj;

      const { used, remaining } = this.actionsStats[index];
      return merge(descriptionObj, {
        activity: { value: used },
        availableCount: remaining,
      });
    },
  },
};
</script>
